/**
 * SLAM AI Evaluation - VLM Biomechanical Analysis Dashboard
 *
 * Displays comprehensive evaluation of Gemini 2.5 Pro pitch tipping detection
 * for Orioles pitching staff (Flaherty, Mize, Skubal).
 *
 * Audience: Data Scientists, Engineers, Head Coaches
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Brain,
  Target,
  TrendingUp,
  Zap,
  ChevronDown,
  ChevronUp,
  Activity,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  FlaskConical,
  Database,
  Cpu,
  Eye,
  Play,
  Video,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';

import { GlassCard, BentoCard, BentoStat, ResponsiveBentoGrid } from './BentoGrid';
import CircularGauge from './CircularGauge';
import { getVideoUrl } from '../../lib/wireDataLoader';
import { getConfidenceLevel, getConfidenceColor } from '../../lib/wireTypes';
import BiomechanicsVideoModal from './BiomechanicsVideoModal';

// Import the evaluation data
import biomechanicsData from '../../data/evaluation/biomechanics_summary.json';

// =============================================================================
// CONSTANTS
// =============================================================================

const PITCH_TYPE_NAMES = {
  FF: '4-Seam Fastball',
  FT: '2-Seam / Sinker',
  CB: 'Curveball',
  SL: 'Slider',
  CH: 'Changeup',
  SP: 'Splitter',
  SW: 'Sweeper',
};

const CONFIDENCE_COLORS = {
  CRITICAL: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  HIGH: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  MEDIUM: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  LOW: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
  NONE: { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/30' },
};

// Orioles color scheme
const ORIOLES = {
  orange: '#DF4601',
  orangeLight: '#FF6B1A',
  black: '#000000',
  gold: '#E8B800',
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function getTippingRateColor(rate) {
  if (rate >= 90) return 'text-red-400';
  if (rate >= 70) return 'text-orange-400';
  if (rate >= 50) return 'text-yellow-400';
  return 'text-green-400';
}

function getTippingRateBg(rate) {
  if (rate >= 90) return 'bg-red-500/10 border-red-500/20';
  if (rate >= 70) return 'bg-orange-500/10 border-orange-500/20';
  if (rate >= 50) return 'bg-yellow-500/10 border-yellow-500/20';
  return 'bg-green-500/10 border-green-500/20';
}

// =============================================================================
// TELL VIDEO CAROUSEL - Mini carousel showing videos with a specific tell
// =============================================================================

function TellVideoCarousel({ tellName, videos, onVideoClick }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollable = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    checkScrollable();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScrollable);
      return () => el.removeEventListener('scroll', checkScrollable);
    }
  }, [videos]);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const cardWidth = 140;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -cardWidth * 2 : cardWidth * 2,
      behavior: 'smooth'
    });
  };

  if (!videos || videos.length === 0) return null;

  return (
    <div className="mt-3 relative">
      <div className="flex items-center gap-2 mb-2">
        <Video className="w-3 h-3 text-blue-400" />
        <span className="text-[10px] text-zinc-400">
          {videos.length} video{videos.length !== 1 ? 's' : ''} showing this tell
        </span>
      </div>

      {/* Scroll Controls */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-zinc-900/90 hover:bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700 mt-3"
        >
          <ChevronLeft className="w-4 h-4 text-zinc-300" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-zinc-900/90 hover:bg-zinc-800 rounded-full flex items-center justify-center border border-zinc-700 mt-3"
        >
          <ChevronRight className="w-4 h-4 text-zinc-300" />
        </button>
      )}

      {/* Carousel */}
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto scrollbar-hide pb-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {videos.map((video, idx) => (
          <TellVideoCard
            key={video.gcsUri || idx}
            video={video}
            tellName={tellName}
            onClick={() => onVideoClick?.(video)}
          />
        ))}
      </div>
    </div>
  );
}

function TellVideoCard({ video, tellName, onClick }) {
  const videoRef = useRef(null);
  const [thumbnailReady, setThumbnailReady] = useState(false);
  const videoUrl = video.gcsUri ? getVideoUrl(video.gcsUri) : null;
  const level = getConfidenceLevel(video.confidence);
  const color = getConfidenceColor(level);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || !videoUrl) return;

    const handleLoadedMetadata = () => {
      videoEl.currentTime = 0.5;
    };
    const handleSeeked = () => {
      setThumbnailReady(true);
    };

    videoEl.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoEl.addEventListener('seeked', handleSeeked);

    return () => {
      videoEl.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoEl.removeEventListener('seeked', handleSeeked);
    };
  }, [videoUrl]);

  return (
    <button
      onClick={onClick}
      className="flex-shrink-0 w-32 group relative rounded-lg overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-orange-500/50 transition-all"
    >
      {/* Video Thumbnail */}
      <div className="aspect-video relative bg-zinc-900">
        {videoUrl ? (
          <video
            ref={videoRef}
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            className={`w-full h-full object-cover transition-opacity ${thumbnailReady ? 'opacity-100' : 'opacity-0'}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video className="w-6 h-6 text-zinc-600" />
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Play className="w-6 h-6 text-white" fill="white" />
        </div>

        {/* Confidence badge */}
        <div
          className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-[9px] font-bold"
          style={{ backgroundColor: color + '33', color }}
        >
          {(video.confidence * 100).toFixed(0)}%
        </div>

        {/* Pitch type badge */}
        <div className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-black/70 rounded text-[9px] font-mono text-white">
          {video.pitchType || 'UNK'}
        </div>
      </div>
    </button>
  );
}

// =============================================================================
// EXECUTIVE SUMMARY CARD
// =============================================================================

function ExecutiveSummary({ data }) {
  const totalPitches = data.total_predictions;
  const pitcherCount = Object.keys(data.pitchers).length;

  // Calculate overall stats
  const allPitchTypes = [];
  let totalTipping = 0;
  let totalSamples = 0;

  Object.values(data.pitchers).forEach(pitcher => {
    Object.values(pitcher.pitch_types).forEach(pt => {
      allPitchTypes.push(pt);
      totalTipping += pt.tipping_detected;
      totalSamples += pt.count;
    });
  });

  const overallTippingRate = totalSamples > 0 ? (totalTipping / totalSamples * 100).toFixed(1) : 0;
  const avgConfidence = (allPitchTypes.reduce((sum, pt) => sum + pt.avg_confidence, 0) / allPitchTypes.length * 100).toFixed(1);

  return (
    <GlassCard className="p-6" opacity={50}>
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center ring-1 ring-orange-500/30">
            <Brain className="w-6 h-6 text-orange-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">SLAM AI Evaluation</h2>
            <p className="text-sm text-zinc-400">Gemini 2.5 Pro VLM Biomechanical Analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 text-xs font-mono bg-orange-500/10 text-orange-400 rounded border border-orange-500/20">
            v2.0
          </span>
          <span className="px-2 py-1 text-xs font-mono bg-zinc-700 text-zinc-300 rounded">
            Jan 2026
          </span>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Database className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Total Analyzed</span>
          </div>
          <p className="text-3xl font-bold font-mono text-white">{totalPitches.toLocaleString()}</p>
          <p className="text-xs text-zinc-500 mt-1">pitch videos</p>
        </div>

        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-purple-400" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Pitchers</span>
          </div>
          <p className="text-3xl font-bold font-mono text-white">{pitcherCount}</p>
          <p className="text-xs text-zinc-500 mt-1">evaluated</p>
        </div>

        <div className={`rounded-xl p-4 border ${getTippingRateBg(parseFloat(overallTippingRate))}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Avg Tipping Rate</span>
          </div>
          <p className={`text-3xl font-bold font-mono ${getTippingRateColor(parseFloat(overallTippingRate))}`}>
            {overallTippingRate}%
          </p>
          <p className="text-xs text-zinc-500 mt-1">detection rate</p>
        </div>

        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-green-400" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Avg Confidence</span>
          </div>
          <p className="text-3xl font-bold font-mono text-green-400">{avgConfidence}%</p>
          <p className="text-xs text-zinc-500 mt-1">model certainty</p>
        </div>
      </div>
    </GlassCard>
  );
}

// =============================================================================
// PITCHER CARD COMPONENT
// =============================================================================

function PitcherCard({ name, data, isExpanded, onToggle, videos = [], onVideoClick }) {
  const pitchTypes = Object.entries(data.pitch_types);
  const totalPitches = pitchTypes.reduce((sum, [, pt]) => sum + pt.count, 0);
  const avgTippingRate = (pitchTypes.reduce((sum, [, pt]) => sum + pt.tipping_rate, 0) / pitchTypes.length).toFixed(1);
  const avgConfidence = (pitchTypes.reduce((sum, [, pt]) => sum + pt.avg_confidence, 0) / pitchTypes.length * 100).toFixed(1);

  // Sort pitch types by tipping rate
  const sortedPitchTypes = [...pitchTypes].sort((a, b) => b[1].tipping_rate - a[1].tipping_rate);

  // Filter videos for this pitcher
  // If no pitcher match found, use all videos (for cross-team analysis)
  const pitcherVideos = useMemo(() => {
    if (!videos || videos.length === 0) return [];

    // Try to match by pitcher name
    const matched = videos.filter(v =>
      v.pitcher?.toLowerCase().includes(name.toLowerCase()) ||
      name.toLowerCase().includes(v.pitcher?.toLowerCase() || '')
    );

    // If no match found, return all videos (allows cross-data source viewing)
    return matched.length > 0 ? matched : videos;
  }, [videos, name]);

  return (
    <GlassCard className="overflow-hidden" opacity={40}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Avatar/Initial */}
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-500/30 to-orange-600/20 flex items-center justify-center ring-1 ring-orange-500/30">
              <span className="text-2xl font-bold text-orange-400">{name[0]}</span>
            </div>

            <div>
              <h3 className="text-lg font-bold text-white">{name}</h3>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-zinc-400">{totalPitches} pitches</span>
                <span className="text-sm text-zinc-600">|</span>
                <span className="text-sm text-zinc-400">{pitchTypes.length} pitch types</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Quick Stats */}
            <div className="hidden md:flex items-center gap-3">
              <div className="text-right">
                <p className={`text-lg font-bold font-mono ${getTippingRateColor(parseFloat(avgTippingRate))}`}>
                  {avgTippingRate}%
                </p>
                <p className="text-xs text-zinc-500">Avg Tipping</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold font-mono text-blue-400">{avgConfidence}%</p>
                <p className="text-xs text-zinc-500">Avg Conf</p>
              </div>
            </div>

            {/* Expand Toggle */}
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
              isExpanded ? 'bg-orange-500/20' : 'bg-zinc-700/50'
            }`}>
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-orange-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-zinc-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-zinc-700/50 p-4 bg-zinc-900/30">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
            {sortedPitchTypes.map(([pitchCode, pitchData]) => (
              <PitchTypeCard
                key={pitchCode}
                code={pitchCode}
                data={pitchData}
                videos={pitcherVideos}
                onVideoClick={onVideoClick}
              />
            ))}
          </div>
        </div>
      )}
    </GlassCard>
  );
}

// =============================================================================
// PITCH TYPE CARD
// =============================================================================

function PitchTypeCard({ code, data, videos = [], onVideoClick }) {
  const [showTells, setShowTells] = useState(false);
  const [expandedTellIdx, setExpandedTellIdx] = useState(null);
  const confidenceDist = data.confidence_distribution || {};

  // Group videos by tell for this pitch type
  const videosByTell = useMemo(() => {
    if (!videos || videos.length === 0) return {};
    const grouped = {};
    videos.forEach(video => {
      if (video.pitchType !== code) return;
      // Support both field names: indicators (newer) and tippingIndicators (legacy)
      const indicators = video.indicators || video.tippingIndicators || [];
      indicators.forEach(ind => {
        const tellName = ind.tell || ind.category || 'Unknown';
        if (!grouped[tellName]) grouped[tellName] = [];
        grouped[tellName].push(video);
      });
    });
    return grouped;
  }, [videos, code]);

  return (
    <div className={`rounded-xl border p-4 transition-all ${getTippingRateBg(data.tipping_rate)}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 text-sm font-bold font-mono bg-orange-500/20 text-orange-400 rounded">
            {code}
          </span>
          <span className="text-sm text-zinc-300">{PITCH_TYPE_NAMES[code] || code}</span>
        </div>
        <CircularGauge
          value={data.tipping_rate}
          maxValue={100}
          size="sm"
          inverted={true}
          showPercentage={true}
        />
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
          <p className="text-lg font-bold font-mono text-white">{data.count}</p>
          <p className="text-[10px] text-zinc-500 uppercase">Samples</p>
        </div>
        <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
          <p className="text-lg font-bold font-mono text-orange-400">{data.tipping_detected}</p>
          <p className="text-[10px] text-zinc-500 uppercase">Tipping</p>
        </div>
        <div className="text-center p-2 bg-zinc-900/50 rounded-lg">
          <p className="text-lg font-bold font-mono text-blue-400">{(data.avg_confidence * 100).toFixed(0)}%</p>
          <p className="text-[10px] text-zinc-500 uppercase">Confidence</p>
        </div>
      </div>

      {/* Confidence Distribution */}
      <div className="flex flex-wrap gap-1 mb-3">
        {Object.entries(confidenceDist).map(([level, count]) => {
          const colors = CONFIDENCE_COLORS[level] || CONFIDENCE_COLORS.NONE;
          return (
            <span
              key={level}
              className={`px-2 py-0.5 text-xs font-mono rounded ${colors.bg} ${colors.text} border ${colors.border}`}
            >
              {level}: {count}
            </span>
          );
        })}
      </div>

      {/* Top Tells Toggle */}
      {data.top_biomechanical_tells?.length > 0 && (
        <>
          <button
            onClick={() => {
              setShowTells(!showTells);
              if (showTells) setExpandedTellIdx(null); // Reset expanded tell when closing
            }}
            className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-200 transition-colors py-2 border-t border-zinc-700/50"
          >
            <span className="flex items-center gap-1">
              <Zap className="w-3 h-3" />
              {data.top_biomechanical_tells.length} Biomechanical Tells
            </span>
            {showTells ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showTells && (
            <div className="space-y-2 mt-2">
              {data.top_biomechanical_tells.slice(0, 5).map((tell, idx) => {
                const isExpanded = expandedTellIdx === idx;
                const hasLongDescription = tell.example_description && tell.example_description.length > 120;

                return (
                  <div key={idx} className="p-2 bg-zinc-900/50 rounded-lg border-l-2 border-orange-500/50">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-white">{tell.tell}</span>
                      <span className="text-[10px] text-zinc-500">
                        {tell.frequency}x | {(tell.avg_confidence * 100).toFixed(0)}%
                      </span>
                    </div>
                    {tell.example_description && (
                      <div>
                        <p className={`text-[11px] text-zinc-400 ${isExpanded ? '' : 'line-clamp-2'}`}>
                          {tell.example_description}
                        </p>
                        {hasLongDescription && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTellIdx(isExpanded ? null : idx);
                            }}
                            className="text-[10px] text-orange-400 hover:text-orange-300 mt-1 font-medium"
                          >
                            {isExpanded ? '← Show less' : 'Read more →'}
                          </button>
                        )}
                      </div>
                    )}

                    {/* Video carousel for this tell */}
                    {videosByTell[tell.tell]?.length > 0 && (
                      <TellVideoCarousel
                        tellName={tell.tell}
                        videos={videosByTell[tell.tell]}
                        onVideoClick={onVideoClick}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// METHODOLOGY SECTION
// =============================================================================

function MethodologySection() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <GlassCard className="p-4" opacity={30}>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center ring-1 ring-purple-500/30">
            <FlaskConical className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Technical Methodology</h3>
            <p className="text-xs text-zinc-500">VLM Analysis Pipeline & Configuration</p>
          </div>
        </div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
          isExpanded ? 'bg-purple-500/20' : 'bg-zinc-700/50'
        }`}>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-purple-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-zinc-700/50 space-y-4">
          {/* Model Configuration */}
          <div>
            <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Cpu className="w-3 h-3" />
              Model Configuration
            </h4>
            <div className="bg-zinc-900/50 rounded-lg p-3 font-mono text-xs overflow-x-auto">
              <pre className="text-zinc-300">{`{
  "model": "gemini-2.5-pro",
  "maxOutputTokens": 8192,
  "temperature": 0.3,
  "systemInstruction": "THE WIRE pitch tipping detection",
  "batchMode": "async",
  "videoDurationRange": "9-18 seconds"
}`}</pre>
            </div>
          </div>

          {/* Confidence Thresholds */}
          <div>
            <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2 flex items-center gap-2">
              <BarChart3 className="w-3 h-3" />
              Confidence Thresholds
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { level: 'CRITICAL', threshold: '>85%', desc: 'Actively exploited' },
                { level: 'HIGH', threshold: '70-85%', desc: 'Advanced scouting' },
                { level: 'MEDIUM', threshold: '50-70%', desc: 'Expert analysis' },
                { level: 'LOW', threshold: '<50%', desc: 'Marginal tell' },
                { level: 'NONE', threshold: '0%', desc: 'No variance' },
              ].map(({ level, threshold, desc }) => {
                const colors = CONFIDENCE_COLORS[level];
                return (
                  <div key={level} className={`p-2 rounded-lg ${colors.bg} border ${colors.border}`}>
                    <p className={`text-xs font-bold ${colors.text}`}>{level}</p>
                    <p className="text-[10px] text-zinc-400 font-mono">{threshold}</p>
                    <p className="text-[10px] text-zinc-500 mt-1">{desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Key Metrics */}
          <div>
            <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Activity className="w-3 h-3" />
              Biomechanical Metrics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
              {[
                { metric: 'Contralateral Trunk Tilt (CTT)', threshold: '>3 variance', desc: 'Detectable by elite hitters' },
                { metric: 'Release Point Consistency', threshold: '>2 inch variance', desc: 'Significant tell indicator' },
                { metric: 'Arm Speed Differential', threshold: '>5%', desc: 'Detectable by elite hitters' },
                { metric: 'Forward Trunk Tilt', threshold: '16-19% variance', desc: 'Biomechanically significant' },
              ].map(({ metric, threshold, desc }) => (
                <div key={metric} className="p-2 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                  <p className="font-semibold text-white">{metric}</p>
                  <p className="text-zinc-400 font-mono">{threshold}</p>
                  <p className="text-zinc-500 text-[10px] mt-1">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Multi-Expert System */}
          <div>
            <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Eye className="w-3 h-3" />
              Mixture of Experts System
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { expert: 'Biomechanics', weight: '0.25', areas: 'CTT, arm slot, hip-shoulder separation' },
                { expert: 'Visual Analysis', weight: '0.25', areas: 'Glove position, grip visibility, head tilt' },
                { expert: 'Coaching', weight: 'Variable', areas: 'Actionable corrections, drill recommendations' },
              ].map(({ expert, weight, areas }) => (
                <div key={expert} className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-white text-sm">{expert}</p>
                    <span className="px-1.5 py-0.5 text-[10px] font-mono bg-purple-500/20 text-purple-400 rounded">
                      {weight}
                    </span>
                  </div>
                  <p className="text-[11px] text-zinc-400">{areas}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </GlassCard>
  );
}

// =============================================================================
// CROSS-PITCHER PATTERNS
// =============================================================================

function CrossPitcherPatterns({ data }) {
  // Aggregate patterns across all pitchers
  const patterns = useMemo(() => {
    const tellFrequency = {};
    const pitchCategoryStats = {
      fastballs: { codes: ['FF', 'FT'], samples: 0, tipping: 0, count: 0 },
      breaking: { codes: ['CB', 'SL', 'SW'], samples: 0, tipping: 0, count: 0 },
      offspeed: { codes: ['CH', 'SP'], samples: 0, tipping: 0, count: 0 },
    };

    Object.values(data.pitchers).forEach(pitcher => {
      Object.entries(pitcher.pitch_types).forEach(([code, pt]) => {
        // Aggregate tell types
        pt.top_biomechanical_tells?.forEach(tell => {
          const key = tell.tell;
          if (!tellFrequency[key]) {
            tellFrequency[key] = { count: 0, totalConf: 0, pitchers: new Set() };
          }
          tellFrequency[key].count += tell.frequency;
          tellFrequency[key].totalConf += tell.avg_confidence * tell.frequency;
        });

        // Aggregate by pitch category
        Object.values(pitchCategoryStats).forEach(cat => {
          if (cat.codes.includes(code)) {
            cat.samples += pt.count;
            cat.tipping += pt.tipping_detected;
            cat.count += 1;
          }
        });
      });
    });

    // Calculate category averages
    Object.values(pitchCategoryStats).forEach(cat => {
      cat.tippingRate = cat.samples > 0 ? (cat.tipping / cat.samples * 100).toFixed(1) : 0;
    });

    // Sort tells by frequency
    const sortedTells = Object.entries(tellFrequency)
      .map(([name, stats]) => ({
        name,
        frequency: stats.count,
        avgConfidence: stats.count > 0 ? (stats.totalConf / stats.count).toFixed(3) : 0,
      }))
      .sort((a, b) => b.frequency - a.frequency)
      .slice(0, 8);

    return { tellFrequency: sortedTells, pitchCategoryStats };
  }, [data]);

  return (
    <GlassCard className="p-4" opacity={40}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center ring-1 ring-blue-500/30">
          <TrendingUp className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-white">Cross-Pitcher Analysis</h3>
          <p className="text-xs text-zinc-500">Universal patterns identified across all pitchers</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pitch Category Performance */}
        <div>
          <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-3">
            Detection by Pitch Category
          </h4>
          <div className="space-y-2">
            {[
              { name: 'Fastballs (FF, FT)', ...patterns.pitchCategoryStats.fastballs },
              { name: 'Breaking Balls (CB, SL, SW)', ...patterns.pitchCategoryStats.breaking },
              { name: 'Off-Speed (CH, SP)', ...patterns.pitchCategoryStats.offspeed },
            ].map(({ name, samples, tipping, tippingRate }) => (
              <div key={name} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
                <div>
                  <p className="text-sm font-medium text-white">{name}</p>
                  <p className="text-xs text-zinc-500">{samples} samples | {tipping} tipping</p>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-bold font-mono ${getTippingRateColor(parseFloat(tippingRate))}`}>
                    {tippingRate}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Universal Tells */}
        <div>
          <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-3">
            Most Common Tell Types
          </h4>
          <div className="space-y-1">
            {patterns.tellFrequency.map(({ name, frequency, avgConfidence }, idx) => (
              <div
                key={name}
                className="flex items-center justify-between p-2 bg-zinc-800/30 rounded-lg hover:bg-zinc-800/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-zinc-500 bg-zinc-700/50 rounded">
                    {idx + 1}
                  </span>
                  <span className="text-xs text-zinc-300 truncate max-w-[200px]">{name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-500">{frequency}x</span>
                  <span className="text-xs font-mono text-orange-400">{(parseFloat(avgConfidence) * 100).toFixed(0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function SlamAIEvaluation({ analyses = [], onVideoClick }) {
  const [expandedPitchers, setExpandedPitchers] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const data = biomechanicsData;

  // Get videos for the selected pitch type (for navigation)
  const videosForCurrentPitchType = useMemo(() => {
    if (!selectedVideo || !analyses.length) return [];
    return analyses.filter(a => a.pitchType === selectedVideo.pitchType);
  }, [selectedVideo, analyses]);

  // Get all biomechanical tells for the current pitch type (from summary data)
  const tellsForCurrentPitchType = useMemo(() => {
    if (!selectedVideo) return [];
    const pitchType = selectedVideo.pitchType;
    const allTells = [];

    Object.values(data.pitchers || {}).forEach(pitcher => {
      const ptData = pitcher.pitch_types?.[pitchType];
      if (ptData?.top_biomechanical_tells) {
        ptData.top_biomechanical_tells.forEach(tell => {
          if (!allTells.find(t => t.tell === tell.tell)) {
            allTells.push({
              tell: tell.tell,
              confidence: tell.avg_confidence,
              description: tell.example_description,
              frequency: tell.frequency,
            });
          }
        });
      }
    });

    return allTells;
  }, [selectedVideo, data.pitchers]);

  const togglePitcher = (name) => {
    setExpandedPitchers(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  // Handle video click - either use provided handler or local state
  const handleVideoClick = (video) => {
    if (onVideoClick) {
      onVideoClick(video);
    } else {
      setSelectedVideo(video);
      // Find index in filtered list
      const filtered = analyses.filter(a => a.pitchType === video.pitchType);
      const idx = filtered.findIndex(a => a.videoUid === video.videoUid || a.gcsUri === video.gcsUri);
      setSelectedVideoIndex(idx >= 0 ? idx : 0);
    }
  };

  // Navigation handlers
  const handleNavigatePrev = () => {
    if (selectedVideoIndex > 0 && videosForCurrentPitchType.length > 0) {
      const newIdx = selectedVideoIndex - 1;
      setSelectedVideoIndex(newIdx);
      setSelectedVideo(videosForCurrentPitchType[newIdx]);
    }
  };

  const handleNavigateNext = () => {
    if (selectedVideoIndex < videosForCurrentPitchType.length - 1) {
      const newIdx = selectedVideoIndex + 1;
      setSelectedVideoIndex(newIdx);
      setSelectedVideo(videosForCurrentPitchType[newIdx]);
    }
  };

  // Auto-expand first pitcher
  useEffect(() => {
    if (data.pitchers) {
      const firstPitcher = Object.keys(data.pitchers)[0];
      if (firstPitcher) {
        setExpandedPitchers({ [firstPitcher]: true });
      }
    }
  }, [data]);

  return (
    <div className="space-y-4">
      {/* Executive Summary */}
      <ExecutiveSummary data={data} />

      {/* Cross-Pitcher Patterns */}
      <CrossPitcherPatterns data={data} />

      {/* Pitcher Cards */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-white uppercase tracking-wide">Pitcher Breakdown</h2>
          <span className="text-xs text-zinc-500">Click to expand</span>
          {analyses.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-xs">
              {analyses.length} videos available
            </span>
          )}
        </div>

        {Object.entries(data.pitchers).map(([name, pitcherData]) => (
          <PitcherCard
            key={name}
            name={name}
            data={pitcherData}
            isExpanded={expandedPitchers[name]}
            onToggle={() => togglePitcher(name)}
            videos={analyses}
            onVideoClick={handleVideoClick}
          />
        ))}
      </div>

      {/* Methodology */}
      <MethodologySection />

      {/* Footer */}
      <div className="flex items-center justify-between py-4 border-t border-zinc-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center">
            <span className="text-sm font-bold text-orange-400">S</span>
          </div>
          <div>
            <p className="text-xs font-semibold text-white">SLAM Sports AI</p>
            <p className="text-[10px] text-zinc-500">THE WIRE Evaluation System</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-zinc-500">Generated: January 27, 2026</p>
          <p className="text-[10px] text-zinc-500">Model: Gemini 2.5 Pro</p>
        </div>
      </div>

      {/* Sophisticated Video Modal with Biomechanical Markers */}
      {selectedVideo && !onVideoClick && (
        <BiomechanicsVideoModal
          analysis={selectedVideo}
          allIndicatorsForPitchType={tellsForCurrentPitchType}
          onClose={() => setSelectedVideo(null)}
          onNavigatePrev={handleNavigatePrev}
          onNavigateNext={handleNavigateNext}
          currentIndex={selectedVideoIndex}
          totalCount={videosForCurrentPitchType.length}
        />
      )}
    </div>
  );
}
